// src/components/MovimentacaoCaixaModal.jsx
import React, { useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import {
  FaSave,
  FaArrowCircleUp,
  FaArrowCircleDown,
  FaTag,
  FaRegStickyNote,
  FaDollarSign,
} from 'react-icons/fa';

const MovimentacaoCaixaModal = ({ isOpen, onClose, onSave }) => {
  const [tipo, setTipo] = useState('entrada');
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('');
  const [valor, setValor] = useState('');
  const [erro, setErro] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const v = parseFloat(String(valor).replace(',', '.'));
    if (!categoria || !descricao || !Number.isFinite(v) || v <= 0) {
      setErro('Preencha categoria, descrição e um valor válido maior que zero.');
      return;
    }
    setErro('');
    onSave({
      tipo,
      descricao,
      categoria,
      valor: v,
      data: Timestamp.now(),
    });
    // reset
    setTipo('entrada');
    setDescricao('');
    setCategoria('');
    setValor('');
  };

  const closeOnBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein"
      onClick={closeOnBackdrop}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-xl relative animate-modalSlideIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-h-[94vh] flex flex-col">
          {/* Header (padrão com botão Fechar) */}
          <div className="p-7 pb-0 relative">
            <button
              className="absolute right-8 top-7 text-gray-500 hover:text-blue-700 dark:text-gray-200 dark:hover:text-white text-base font-medium"
              onClick={onClose}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-blue-600 dark:text-white">
              Nova Movimentação
            </h2>
          </div>

          {/* Body (rolável) */}
          <form onSubmit={handleSubmit} className="px-7 pb-6 space-y-6 overflow-y-auto flex-1">
            {/* Toggle Entrada/Saída */}
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">
                Tipo de Movimentação
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setTipo('entrada')}
                  className={`py-3 rounded-xl font-semibold border-2 transition-all flex items-center justify-center gap-2 text-base
                    ${
                      tipo === 'entrada'
                        ? 'bg-green-50 border-green-600 text-green-700 shadow'
                        : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  aria-pressed={tipo === 'entrada'}
                >
                  <FaArrowCircleUp /> Entrada
                </button>
                <button
                  type="button"
                  onClick={() => setTipo('saida')}
                  className={`py-3 rounded-xl font-semibold border-2 transition-all flex items-center justify-center gap-2 text-base
                    ${
                      tipo === 'saida'
                        ? 'bg-red-50 border-red-600 text-red-700 shadow'
                        : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  aria-pressed={tipo === 'saida'}
                >
                  <FaArrowCircleDown /> Saída
                </button>
              </div>
            </div>

            {/* Categoria */}
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">Categoria</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <FaTag />
                </span>
                <input
                  type="text"
                  value={categoria}
                  onChange={(e) => {
                    setCategoria(e.target.value);
                    if (erro) setErro('');
                  }}
                  placeholder="Ex: Venda Balcão, Pagamento Fornecedor"
                  className="border border-gray-300 dark:border-gray-700 pl-10 py-3 rounded-xl w-full bg-gray-50 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#004AAD] outline-none transition"
                />
              </div>
            </div>

            {/* Descrição */}
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">Descrição</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <FaRegStickyNote />
                </span>
                <input
                  type="text"
                  value={descricao}
                  onChange={(e) => {
                    setDescricao(e.target.value);
                    if (erro) setErro('');
                  }}
                  placeholder="Detalhe do lançamento"
                  className="border border-gray-300 dark:border-gray-700 pl-10 py-3 rounded-xl w-full bg-gray-50 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#004AAD] outline-none transition"
                />
              </div>
            </div>

            {/* Valor */}
            <div>
              <label className="block text-sm font-semibold mb-2 dark:text-gray-200">Valor (R$)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <FaDollarSign />
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={valor}
                  onChange={(e) => {
                    setValor(e.target.value);
                    if (erro) setErro('');
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                  placeholder="0,00"
                  className="border border-gray-300 dark:border-gray-700 pl-10 py-3 rounded-xl w-full bg-gray-50 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#004AAD] outline-none transition"
                />
              </div>
            </div>

            {/* Erro */}
            {erro && <p className="text-red-600 dark:text-red-400 text-sm">{erro}</p>}
          </form>

          {/* Rodapé (separador + botão primário azul) */}
          <div className="px-7 py-5 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={handleSubmit}
              type="button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
            >
              <FaSave /> Salvar Movimentação
            </button>
          </div>
        </div>

        {/* Animações */}
        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
};

export default MovimentacaoCaixaModal;
